<template>
    <editor-page class="translation-manager" title="Translation Manager" no-padding>
        <template v-if="!loading">
            <b-navbar class="editor-menu bg-white p-2">
                <b-nav class="d-flex align-items-center bg-white">
                    <b-btn variant="light" class="mr-3" @click="goBack">
                        <i class="fas fa-arrow-left mr-1"></i>
                        <span v-tr>Back|Retour</span>
                    </b-btn>
                    <b-btn variant="primary" @click="saveAll">
                        <i class="fas fa-save mr-1" />
                        <span v-tr>Save|Enregistrer</span>
                    </b-btn>
                </b-nav>

                <b-nav class="mx-auto">
                    <div class="d-flex align-items-center">
                        <b-btn-group class="bg-light rounded d-inline-flex align-items-center">
                            <b-dropdown size="sm" id="translation-manager-menu" variant="light" no-caret>
                                <template #button-content>
                                    <span v-tr>Translation Manager|Gestionnaire de traduction</span>
                                </template>
                            </b-dropdown>

                            <b-dropdown size="sm" id="website-menu" variant="light" no-caret class="border-left">
                                <template #button-content>
                                    <b-img width="24" height="24" style="margin: -4px 0 -2px -2px"
                                        v-if="cmsWebsite?.design?.favicon?.src" :src="cmsWebsite.design.favicon.src" />
                                    <i v-else class="fas fa-globe" />
                                    <span class="ml-2" v-if="isExtraLargeLayout">{{ cmsWebsite?.name | tr }}</span>
                                </template>
                            </b-dropdown>

                            <b-dropdown size="sm" id="page-menu" variant="light" no-caret :disabled="!pageList.length"
                                class="border-left">
                                <template #button-content>
                                    <i class="fas fa-file-lines" />
                                    <span class="ml-2">{{ selectedPageName }}</span>
                                </template>
                                <template #default>
                                    <div class="px-3 py-2" style="min-width: 280px">
                                        <e-search-input v-model="pageSearch" size="sm"
                                            :placeholder="'Search pages|Rechercher des pages' | tr" />
                                    </div>
                                    <b-dropdown-divider />
                                    <div style="max-height: 400px; overflow-y: auto">
                                        <b-dropdown-item v-for="page in filteredPages" :key="page.id"
                                            :active="page.id === selectedPageId" @click="selectPage(page.id)">
                                            <i class="fas fa-file-lines mr-2"></i>
                                            <span>{{ page.name | tr }}</span>
                                        </b-dropdown-item>
                                        <b-dropdown-text v-if="!filteredPages.length" class="text-muted">
                                            <span v-tr>No pages found|Aucune page trouvée</span>
                                        </b-dropdown-text>
                                    </div>
                                </template>
                            </b-dropdown>
                        </b-btn-group>
                    </div>

                    <div class="language-switcher-container ml-3">
                        <div class="language-switcher">
                            <!-- <div class="label" v-tr>Source|Source</div> -->
                            <e-btn-language v-model="sourceLocale" :locales="locales" show-label size="sm" variant="light" />
                        </div>
                        <i class="fa-solid fa-arrow-right" />
                        <div class="language-switcher">
                            <!-- <div class="label" v-tr>Target|Cible</div> -->
                            <e-btn-language v-model="targetLocale" :locales="locales" show-label size="sm" variant="light" />
                        </div>
                        <b-btn size="sm" variant="light" class="swap-btn" @click="swapLocales">
                            <i class="fas fa-shuffle" />
                        </b-btn>
                    </div>
                </b-nav>

                <b-nav>
                    <!-- <b-btn variant="light" class="mr-2" @click="cancelChanges" :disabled="!hasUnsavedChanges">
                        <i class="fas fa-times mr-1" />
                        <span v-tr>Cancel|Annuler</span>
                    </b-btn>
                    <b-btn variant="primary" @click="saveAll">
                        <i class="fas fa-save mr-1" />
                        <span v-tr>Save|Enregistrer</span>
                    </b-btn> -->

                <div class="toolbar-right ml-auto">
                    <b-select v-model="statusFilter" size="sm" class="mr-2" style="max-width: 200px">
                        <option value="all" v-tr>All statuses|Tous les statuts</option>
                        <option value="not_translated" v-tr>Not translated|Non traduit</option>
                        <option value="translated" v-tr>Translated|Traduit</option>
                        <option value="needs_update" v-tr>Needs update|A mettre a jour</option>
                    </b-select>

                    <e-search-input v-model="fieldSearch" style="max-width: 385px; width: 385px"
                        :placeholder="'Search fields|Rechercher un champ' | tr" />
                </div>
                </b-nav>
            </b-navbar>
        </template>

        <div class="editor-container translation-manager-grid">
            <div class="translation-manager-sidebar">
                <b-tabs class="tabs-pills">
                    <b-tab title="Pages" active class="p-3">
                        <e-search-input v-model="pageSearch" :placeholder="'Search pages|Rechercher des pages' | tr" />
                        <b-list-group class="page-list mt-2">
                            <b-list-group-item v-for="page in filteredPages" :key="page.id" button class="page-item"
                                :active="page.id === selectedPageId" @click="selectPage(page.id)">
                                <div class="d-flex align-items-center">
                                    <i class="fas fa-file-lines mr-2"></i>
                                    <span class="page-name">{{ page.name | tr }}</span>
                                </div>
                            </b-list-group-item>
                        </b-list-group>
                    </b-tab>
                    <b-tab title="CMS Items" disabled>
                        <div class="p-3 text-muted" v-tr>Coming soon|Bientot</div>
                    </b-tab>
                </b-tabs>
                <div class="d-flex flex-wrap align-items-center p-3 mt-auto" style="gap: 4px">
                    <b-badge :variant="statusVariant('not_translated')" class="f-9">
                        {{ statusLabel("not_translated") }} {{ statusCounts.not_translated }}
                    </b-badge>
                    <b-badge :variant="statusVariant('needs_update')" class="f-9">
                        {{ statusLabel("needs_update") }} {{ statusCounts.needs_update }}
                    </b-badge>
                    <b-badge :variant="statusVariant('same')" class="f-9">
                        {{ statusLabel("same") }} {{ statusCounts.same }}
                    </b-badge>
                    <b-badge :variant="statusVariant('translated')" class="f-9">
                        {{ statusLabel("translated") }} {{ statusCounts.translated }}
                    </b-badge>
                </div>
            </div>

            <!-- <editor-submenu style="border-top: 1px solid #ddd;">

            </editor-submenu> -->

            <!-- <div class="translation-toolbar">

                <div class="toolbar-right">
                    <b-select v-model="statusFilter" size="sm" class="mr-2">
                        <option value="all" v-tr>All statuses|Tous les statuts</option>
                        <option value="not_translated" v-tr>Not translated|Non traduit</option>
                        <option value="translated" v-tr>Translated|Traduit</option>
                        <option value="needs_update" v-tr>Needs update|A mettre a jour</option>
                    </b-select>

                    <e-search-input v-model="fieldSearch" style="width: 320px"
                        :placeholder="'Search fields|Rechercher un champ' | tr" />
                </div>
            </div> -->

            <div class="translation-body">
                <section class="translation-content">
                    <div class="content-header">
                        <div class="content-columns">
                            <div class="col-label meta"></div>
                            <div class="col-label source justify-content-center">
                                <span v-tr>Source|Source</span>
                                <b-img class="ml-1" style="width: 20px" :src="getLanguageFlag(sourceLocale)"></b-img>
                                <span class="ml-1">{{ sourceLocale }}</span>
                            </div>
                            <div class="col-label target justify-content-center">
                                <span v-tr>Target|Cible</span>
                                <b-img class="ml-1" style="width: 20px" :src="getLanguageFlag(targetLocale)"></b-img>
                                <span class="ml-1">{{ targetLocale }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="content-body" v-if="groupedComponents.length">
                        <div v-for="group in filteredGroups" :key="group.block.id" class="block-group">
                            <div class="block-group-header">
                                <i class="fas mr-2" :class="group.block.typeDef?.glyph || 'fa-grid-2'"></i>
                                <span class="block-label">{{ group.block.type }} - </span>
                                <span class="block-sublabel">{{ group.block.displayName }}</span>
                                <b-badge variant="light" class="ml-auto">{{ group.components.length }}</b-badge>
                            </div>

                            <translation-manager-row v-for="item in group.components" :key="item.component.id"
                                :component="item.component" :component-key="item.key" :source-locale="sourceLocale"
                                :target-locale="targetLocale" :translating="!!translatingComponents[item.component.id]"
                                @dirty-changed="onDirtyChanged" @auto-translate="autoTranslateComponent" />
                        </div>
                    </div>

                    <div class="content-empty" v-else>
                        <span v-tr>No translatable fields found|Aucun champ traduisible</span>
                    </div>
                </section>
            </div>
        </div>
    </editor-page>
</template>

<script>
import Vue from "vue";
import swal from "sweetalert";
import CmsPage from "@/helpers/cms/CmsPage.js";
import EditorPage from "@/layout/editor-page.vue";
import EBtnLanguage from "@vue-components/components/e-btn-language.vue";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import TranslationManagerRow from "@/components/editor/translation/translation-manager-row.vue";
import ESearchInput from "@vue-components/components/e-search-input.vue";
import { getLanguageObject } from "@vue-components/helpers/I18nPluginHelper.js";
import EditorSubmenu from "@/components/editor/editor-submenu.vue";

export default {
    name: `translation-manager`,
    components: {
        EditorPage,
        EBtnLanguage,
        TranslationManagerRow,
        ESearchInput,
        EditorSubmenu
    },
    data() {
        return {
            loading: false,
            pageList: [],
            pageSearch: ``,
            selectedPageId: null,
            pageCache: {},
            fieldSearch: ``,
            statusFilter: `all`,
            sourceLocale: `en`,
            targetLocale: `fr`,
            dirtyComponents: {},
            translatingComponents: {},
        };
    },
    computed: {
        locales() {
            return this.cmsWebsite?.settings?.languages || [`en`, `fr`];
        },
        filteredPages() {
            const query = this.pageSearch.trim().toLowerCase();
            if (!query) {
                return this.pageList;
            }
            let result = [];
            for (let page of this.pageList) {
                const name = this.tr(page.name || ``);
                if (String(name).toLowerCase().includes(query)) {
                    result.push(page);
                }
            }
            return result;
        },
        selectedPage() {
            return this.selectedPageId ? this.pageCache[this.selectedPageId] : null;
        },
        selectedPageName() {
            if (!this.selectedPage) {
                return this.$tr(`Select a page|Selectionnez une page`);
            }
            return this.selectedPage.name ? this.tr(this.selectedPage.name) : this.$tr(`Untitled page|Page sans titre`);
        },
        groupedComponents() {
            if (!this.selectedPage) {
                return [];
            }
            let groups = [];
            for (let block of this.selectedPage.blocks) {
                this.collectBlockGroups(groups, block);
            }
            return groups;
        },
        filteredGroups() {
            const query = this.fieldSearch.trim().toLowerCase();
            let result = [];
            for (let group of this.groupedComponents) {
                let filteredComponents = [];
                for (let item of group.components) {
                    if (this.statusFilter !== `all`) {
                        const status = this.getComponentStatus(item);
                        if (status !== this.statusFilter) {
                            continue;
                        }
                    }
                    if (query) {
                        const label = `${group.block.displayName} ${item.key} ${item.component.type}`.toLowerCase();
                        if (!label.includes(query)) {
                            continue;
                        }
                    }
                    filteredComponents.push(item);
                }
                if (filteredComponents.length) {
                    result.push({ block: group.block, components: filteredComponents });
                }
            }
            return result;
        },
        statusCounts() {
            let counts = { translated: 0, not_translated: 0, needs_update: 0, same: 0 };
            for (let group of this.groupedComponents) {
                for (let item of group.components) {
                    const status = this.getComponentStatus(item);
                    counts[status] = (counts[status] || 0) + 1;
                }
            }
            return counts;
        },
        hasUnsavedChanges() {
            return Object.keys(this.dirtyComponents).length > 0;
        },
    },
    watch: {
        selectedPageId(value) {
            if (value) {
                this.loadPage(value);
            }
        },
        cmsWebsite(value) {
            if (value && !this.pageList.length) {
                this.initLocales();
                this.loadPageList();
            }
        },
        sourceLocale(newVal, oldVal) {
            if (newVal === this.targetLocale) {
                this.targetLocale = oldVal;
            }
        },
        targetLocale(newVal, oldVal) {
            if (newVal === this.sourceLocale) {
                this.sourceLocale = oldVal;
            }
        },
    },
    async mounted() {
        this.loading = true;
        this.initLocales();
        await this.loadPageList();

        const routePageId = this.$route.params.pageId;
        if (routePageId) {
            this.selectedPageId = routePageId;
            await this.loadPage(routePageId);
        }
        this.loading = false;
    },
    methods: {
        initLocales() {
            const languages = this.locales;
            if (languages.length >= 2) {
                this.sourceLocale = languages[0];
                this.targetLocale = languages[1];
            } else if (languages.length === 1) {
                this.sourceLocale = languages[0];
                this.targetLocale = languages[0];
            }
        },
        swapLocales() {
            const temp = this.sourceLocale;
            this.sourceLocale = this.targetLocale;
            this.targetLocale = temp;
        },
        collectBlockGroups(groups, block) {
            const components = block.getComponents(`all`, false, false);
            let translatableComponents = [];
            for (let component of components) {
                // Find the key on the block that holds this component
                let componentKey = null;
                for (let key of Object.keys(block)) {
                    if (block[key] === component) {
                        componentKey = key;
                        break;
                    }
                }
                if (component.type === `text`) {
                    translatableComponents.push({ component, key: componentKey });
                } else if (component.type === `image` && component.src && typeof component.src === `object`) {
                    translatableComponents.push({ component, key: componentKey });
                }
            }
            if (translatableComponents.length) {
                groups.push({ block: block, components: translatableComponents });
            }
            for (let child of block.children) {
                this.collectBlockGroups(groups, child);
            }
        },
        getComponentStatus(item) {
            const component = item.component;
            if (component.type === `text`) {
                const value = component.text;
                if (!value || typeof value !== `object`) {
                    return `not_translated`;
                }
                const source = value[this.sourceLocale] || ``;
                const target = value[this.targetLocale] || ``;
                if (!target.trim()) {
                    return `not_translated`;
                }
                if (source === target) {
                    return `same`;
                }
                return `translated`;
            }
            // Image: check both src and alt
            const srcValue = component.src;
            const altValue = component.alt;
            const srcSource = srcValue?.[this.sourceLocale] || ``;
            const srcTarget = srcValue?.[this.targetLocale] || ``;
            const altSource = altValue?.[this.sourceLocale] || ``;
            const altTarget = altValue?.[this.targetLocale] || ``;
            if (!srcTarget && !altTarget) {
                return `not_translated`;
            }
            if (srcSource === srcTarget && altSource === altTarget) {
                return `same`;
            }
            return `translated`;
        },
        statusLabel(status) {
            switch (status) {
                case `not_translated`: return this.$tr(`Not translated|Non traduit`);
                case `needs_update`: return this.$tr(`Needs update|A mettre a jour`);
                case `same`: return this.$tr(`Same|Identique`);
                default: return this.$tr(`Translated|Traduit`);
            }
        },
        statusVariant(status) {
            switch (status) {
                case `not_translated`: return `light-danger`;
                case `needs_update`: return `light-warning`;
                case `same`: return `light-primary`;
                default: return `light-success`;
            }
        },
        getLanguageFlag(locale) {
            return getLanguageObject(locale)?.flag || ``;
        },
        onDirtyChanged({ component, dirty }) {
            if (dirty) {
                Vue.set(this.dirtyComponents, component.id, true);
            } else {
                Vue.delete(this.dirtyComponents, component.id);
            }
        },
        async autoTranslateComponent(component) {
            if (component.type === `image`) {
                return;
            }
            const field = `text`;
            const value = component[field];
            if (!value || typeof value !== `object`) {
                return;
            }
            const source = value[this.sourceLocale];
            if (!source) {
                return;
            }

            Vue.set(this.translatingComponents, component.id, true);

            try {
                const resp = await NetworkV2.post(`/translate`, {
                    text: source,
                    from: this.sourceLocale,
                    to: this.targetLocale,
                }, { system_api: true, timeout: 300000 });
                Vue.set(value, this.targetLocale, resp.data);
                Vue.set(this.dirtyComponents, component.id, true);
            } catch (e) {
                console.error(e);
            } finally {
                Vue.delete(this.translatingComponents, component.id);
            }
        },
        async loadPageList() {
            if (!this.cmsWebsite?.dealerid) {
                return;
            }
            try {
                const resp = await NetworkV2.get(`/api/pages/${this.cmsWebsite.dealerid}/list`);
                this.pageList = (resp.data || []).map((page) => ({
                    id: page._id || page.id,
                    name: page.name,
                    permalink: page.permalink,
                }));
                if (!this.selectedPageId && this.pageList.length) {
                    this.selectedPageId = this.pageList[0].id;
                }
            } catch (e) {
                console.error(e);
            }
        },
        async loadPage(pageId, options = {}) {
            const { force = false } = options;
            if (this.pageCache[pageId] && !force) {
                return;
            }
            try {
                if (force) {
                    Vue.delete(this.pageCache, pageId);
                }
                const resp = await NetworkV2.get(`/api/page/${pageId}`);
                const page = new CmsPage(resp.data, this.cms, this.$route);
                Vue.set(this.pageCache, pageId, page);
            } catch (e) {
                console.error(e);
            }
        },
        async selectPage(pageId) {
            if (pageId === this.selectedPageId) {
                return;
            }
            const canLeave = await this.confirmDiscardChanges();
            if (!canLeave) {
                return;
            }
            this.selectedPageId = pageId;

            const websiteId = this.$route.params.websiteId;
            if (this.$router && websiteId) {
                this.$router.replace({
                    name: `translation-manager-page`,
                    params: { websiteId, pageId },
                }).catch(() => { });
            }
        },
        async confirmDiscardChanges() {
            if (!this.hasUnsavedChanges) {
                return true;
            }
            const result = await swal({
                title: this.$tr(`Unsaved changes|Modifications non enregistrees`),
                text: this.$tr(`If you leave, unsaved changes will be lost.|Si vous quittez, les modifications seront perdues.`),
                icon: `warning`,
                buttons: {
                    cancel: this.$tr(`Stay|Rester`),
                    leave: { text: this.$tr(`Leave|Quitter`) },
                },
            });
            if (result !== `leave`) {
                return false;
            }
            this.dirtyComponents = {};
            return true;
        },
        async cancelChanges() {
            if (!this.hasUnsavedChanges) {
                return;
            }
            const result = await swal({
                title: this.$tr(`Cancel changes?|Annuler les modifications?`),
                text: this.$tr(`All unsaved changes will be lost.|Toutes les modifications seront perdues.`),
                icon: `warning`,
                buttons: {
                    cancel: this.$tr(`Keep editing|Continuer`),
                    confirm: { text: this.$tr(`Cancel changes|Annuler`) },
                },
            });
            if (result !== `confirm`) {
                return;
            }
            if (this.selectedPageId) {
                Vue.delete(this.pageCache, this.selectedPageId);
                await this.loadPage(this.selectedPageId, { force: true });
            }
            this.dirtyComponents = {};
        },
        async goBack() {
            const canLeave = await this.confirmDiscardChanges();
            if (!canLeave) {
                return;
            }
            this.$router.push(`/cms/editor/website/${this.cmsWebsite.id}/${this.selectedPageId}`);
        },
        async saveAll() {
            if (!this.hasUnsavedChanges) {
                swal({
                    title: this.$tr(`Nothing to save|Rien a enregistrer`),
                    icon: `info`
                });
                return;
            }
            if (!this.selectedPage) {
                return;
            }
            try {
                await NetworkV2.post(`/api/pages/edit`, this.selectedPage.toJSON());
                this.dirtyComponents = {};
                this.showSavedMessage(`All changes have been saved.|Toutes les modifications ont été enregistrées.`);
            } catch (e) {
                console.error(e);
                this.showErrorMessage(`An error occurred while saving changes.|Une erreur est survenue lors de l'enregistrement des modifications.`);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.translation-manager {
    height: 100%;
}

.translation-manager-grid {
    display: grid;
    grid-template-areas:
        "toolbar sidebar "
        "body sidebar";
    grid-template-columns: 1fr 320px;
    grid-template-rows: auto 1fr;
    height: calc(100vh - 60px);
    overflow: hidden;
}

.translation-manager-sidebar {
    grid-area: sidebar;
    border-left: 1px solid #ddd;
    border-top: 1px solid #ddd;
    background: #fafafa;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.translation-toolbar {
    grid-area: toolbar;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    background: white;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: auto;
}

.toolbar-right {
    display: flex;
    align-items: center;
}

.translation-body {
    grid-area: body;
    display: flex;
    overflow: hidden;

    border-top: 1px solid #ddd;
}

.page-list .page-name {
    font-weight: 500;
    border-radius: 0;
}

.page-item.active {
    background-color: var(--color-light);
    color: var(--color-primary);
}

.translation-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    --translation-grid: 220px minmax(0, 1fr) minmax(0, 1fr);
}

.content-header {
    display: flex;
    flex-direction: column;
    padding: 12px 16px 8px;
    border-bottom: 1px solid #eee;
    background: white;
}

.header-main {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.header-right {
    display: flex;
    align-items: center;
    margin-left: auto;
}

.content-columns {
    display: grid;
    grid-template-columns: var(--translation-grid);
    gap: 8px;
    padding-top: 8px;
    font-size: 8pt;
    text-transform: uppercase;

    .col-label {
        display: flex;
        align-items: center;
        font-weight: 600;
        font-size: 9pt;
        gap: 4px;
    }
}

.language-switcher-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.language-switcher {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    text-transform: uppercase;
    color: #777;
}

.status-summary {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.content-body {
    padding: 0;
    overflow-y: auto;
    background: #f6f7f9;
}

.block-group {
    margin-bottom: 1px;
}

.block-group-header {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    background: linear-gradient(to right, #e8ecf1, #f0f3f7);
    border-bottom: 1px solid #ddd;
    border-top: 1px solid #ddd;
    color: #4a5568;
    position: sticky;
    top: 0;
    z-index: 1;
    text-transform: capitalize;

    i {
        color: #718096;
    }

    .block-label {
        font-size: 13px;
        font-weight: 600;
    }

    .block-sublabel {
        font-size: 12px;
        color: #555;
        margin-left: 6px;
    }
}

.content-empty {
    padding: 40px;
    text-align: center;
    color: #777;
}

@media (max-width: 992px) {
    .translation-manager-grid {
        grid-template-areas:
            "toolbar"
            "body"
            "sidebar";
        grid-template-columns: 1fr;
        grid-template-rows: auto auto 1fr;
    }

    .translation-manager-sidebar {
        border-right: none;
        border-bottom: 1px solid #eee;
        max-height: 300px;
    }

    .translation-content {
        --translation-grid: 1fr;
    }

    .content-columns {
        display: none;
    }
}

:deep(.tabs.tabs-pills .nav.nav-tabs) {
    border-radius: 0;
    border-bottom: 1px solid #ddd;
    background: #eff2f6;
}
</style>
